import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const InventionNo11Viewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Invention No. 11 BWV 782
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV782/bach-invention-11/bach-invention-11-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Invention No. 11 BWV 782.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-4826">
      <Navbar data-id="element-4827" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-4828">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-4829">
          <img src="https://images.unsplash.com/photo-1552422535-c45813c61732?w=400&h=300&fit=crop" alt="Invention No. 11" className="w-full h-64 md:h-80 object-cover" data-id="element-4830" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-4831" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-4832">
            <div className="max-w-7xl mx-auto" data-id="element-4833">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-4834">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-4835" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-4836">
                Invention No. 11 in G minor
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-4837">BWV 782</p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-4838">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-4839">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-4840">
          <div className="flex items-center space-x-2" data-id="element-4841">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-4842">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-4843" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4844">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-4845">
                  <ZoomInIcon className="h-5 w-5" data-id="element-4846" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-4847">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-4848">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-4849" />
              <span className="hidden sm:inline" data-id="element-4850">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-4851">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-4852" />
              <span className="hidden sm:inline" data-id="element-4853">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-4854">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-4855">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-4856">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Invention No. 11 Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-4857" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-4858">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4859">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-4860">
            <div data-id="element-4861">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4862">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-4863">
                <div data-id="element-4864">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4865">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4866">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-4867">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4868">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4869">
                    c. 1720-1723
                  </dd>
                </div>
                <div data-id="element-4870">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4871">
                    Form
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4872">
                    Two-Part Invention
                  </dd>
                </div>
                <div data-id="element-4873">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4874">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4875">
                    G minor
                  </dd>
                </div>
                <div data-id="element-4876">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4877">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4878">
                    BWV 782
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-4879">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4880">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-4881">
                The eleventh of Bach's fifteen Two-Part Inventions, this piece
                in G minor is notable for its expressive character and elegant
                contrapuntal writing. Written as pedagogical works for his
                eldest son Wilhelm Friedemann, the Inventions demonstrate how to
                play cleanly in two voices and develop musical ideas
                systematically. This invention features a lyrical subject that
                moves gracefully between the hands, creating an intimate
                dialogue. The minor key gives the piece a contemplative,
                melancholic quality, while the flowing sixteenth-note passages
                require smooth legato playing and careful voice leading. The
                work exemplifies Bach's ability to combine technical instruction
                with genuine musical expression, making it both an essential
                study piece and a deeply satisfying performance work.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-4882" />
    </div>;
};